<script lang="ts" setup>
import { intersection } from 'lodash'
import { computed } from 'vue'

import { IncidentId } from '@collector/sportsapi-client-legacy'
import Button from '@mobile/components/Button/Button.vue'
import { getParticipantButtonColor } from '@mobile/components/Button/ButtonUtils'
import SummaryParticipantName from '@mobile/components/Summary/SummaryParticipantName.vue'
import IncidentButton from '@mobile/views/Relation/Shared/IncidentButton/IncidentButton.vue'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

import { useSportConfiguration } from '../../Shared/RelationSportCommonDependencies/useSportConfiguration'
import { useIncidentModals } from '../composables/useIncidentModals'
import { getDetailsPanelPocketIncidents } from './utils'

// composables
const eventInjections = useEventInjections()
const {
  probableEvent,
  probableFirstParticipant,
  probableSecondParticipant,
  sidesSwitched,
} = eventInjections
const { openVIPIncidentTimeModal } = useIncidentModals()
const { eventStatusesConfiguration } = useSportConfiguration()

// data
const pocketIncidents = getDetailsPanelPocketIncidents(
  probableEvent.value.sport_id,
)

// computed
const pocketIncidentsEnabled = computed(() => {
  const cfg = eventStatusesConfiguration[probableEvent.value.status_id]
  const allowedIncidents = cfg?.allowedIncidents

  if (!pocketIncidents || !allowedIncidents) return false

  return intersection(pocketIncidents, allowedIncidents).length > 0
})

// methods
function changeSides(): void {
  sidesSwitched.value = !sidesSwitched.value
}

function handleVIPIncidentClick(
  incidentId: IncidentId,
  participantId: number,
): void {
  openVIPIncidentTimeModal(incidentId, participantId)
}
</script>

<template>
  <div
    v-if="pocketIncidents && pocketIncidents.length"
    class="px-4"
  >
    <div class="rounded-md bg-white shadow-md">
      <div class="flex w-full items-center justify-between px-4 pt-3 font-bold">
        VIP incidents
      </div>
      <div class="relative mt-4 px-4 py-3">
        <div
          v-if="!pocketIncidentsEnabled"
          class="absolute inset-0 z-10 h-full w-full bg-white/50 backdrop-blur"
        >
          <div class="grid h-full w-full place-content-center">
            <span class="text-xl font-bold">
              Incidents cannot be sent for the current event status.
              <br />
              To add an incident, use the manual incident section.
            </span>
          </div>
        </div>
        <div class="flex items-end justify-center space-x-8">
          <div class="flex flex-1 flex-col items-end">
            <SummaryParticipantName
              class="pb-2"
              :color="getParticipantButtonColor(probableFirstParticipant)"
              direction="right"
            >
              {{ probableFirstParticipant.short_name }}
            </SummaryParticipantName>
            <div class="flex flex-wrap justify-end gap-2">
              <IncidentButton
                v-for="(incidentId, key) in pocketIncidents"
                :key="`details-panel-first-participant-pocket-incident-${key}`"
                class="min-w-28"
                :incidentId
                :participantId="probableFirstParticipant.id"
                :sendIncident="false"
                size="md"
                type="vertical"
                @incidentHandled="
                  handleVIPIncidentClick(
                    incidentId,
                    probableFirstParticipant.id,
                  )
                "
              />
            </div>
          </div>
          <div class="pb-3">
            <Button
              size="xs"
              color="blue"
              @click="changeSides"
            >
              <FontAwesomeIcon
                class="mx-1"
                icon="sync"
              />
            </Button>
          </div>
          <div class="flex flex-1 flex-col items-start">
            <SummaryParticipantName
              class="pb-2"
              :color="getParticipantButtonColor(probableSecondParticipant)"
              direction="left"
            >
              {{ probableSecondParticipant.short_name }}
            </SummaryParticipantName>
            <div class="flex flex-wrap gap-2">
              <IncidentButton
                v-for="(incidentId, key) in pocketIncidents"
                :key="`details-panel-second-participant-pocket-incident-${key}`"
                class="min-w-28"
                :incidentId
                :participantId="probableSecondParticipant.id"
                :sendIncident="false"
                size="md"
                type="vertical"
                @incidentHandled="
                  handleVIPIncidentClick(
                    incidentId,
                    probableSecondParticipant.id,
                  )
                "
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
